import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { mainSystems } from '../../config/system';
import { uris } from '../../config/environment';
import loginPKS from '../utils/loginPKS';
import getUrl from '../utils/getUrl';
import './ls-systems-button';
import './ls-systems-subMenu';
import './ls-modal';
import { type RoleInfo } from '../types/leishan';

@customElement('ls-systems-menu')
export class LsSystemsMenu extends LitElement {
    static styles = css`
        .ls-systems-menu {
            display: flex;
            align-items: center;
            height: 50px;
        }

        .ls-systems-menu .background {
            position: absolute;
            top: 50px;
            left: 0;
            width: 100%;
            height: 50px;
            background-color: #dcdfe4;
        }

        .ls-systems-menu.vertical .background {
            display: none !important;
        }

        .ls-systems-menu.vertical .systems-container-inner .systems {
            background-color: #ffffff;
        }

        .ls-systems-menu .title {
            display: none;
            box-sizing: border-box;
            width: 100%;
            cursor: pointer;
        }

        .systems-container {
            position: relative;
            width: 100%;
        }

        .systems-container-inner {
            display: grid;
            grid-template-rows: 1fr;
            transition: all 0.3s ease;
        }

        .systems-container-inner .systems {
            display: flex;
            gap: 15px;
            align-items: center;
            transition: all 0.3s ease;
            background-color: #dcdfe4;
            position: relative;
            z-index: 2;
        }

        .systems-container .active-system {
            display: none;
            width: 100%;
        }

        .-rotate-90 {
            transform: rotate(-90deg);
        }

        .ls-systems-menu.vertical {
            flex-direction: column;
            align-items: flex-start;
            height: auto;
        }

        .ls-systems-menu.vertical .title {
            padding: 9px 15px;
            font-weight: 700;
            display: flex;
            align-items: center;
            gap: 20px;
            cursor: pointer;
        }

        .ls-systems-menu.vertical .title img {
            transition: all 0.3s ease;
        }

        .ls-systems-menu.vertical .systems-container-inner .systems {
            flex-direction: column;
            align-items: flex-start;
            overflow: hidden;
            gap: 2px;
        }

        .ls-systems-menu.vertical .system-item {
            width: 100%;
        }

        .ls-systems-menu.vertical .systems-container-inner.deactivated {
            grid-template-rows: 0fr;
        }

        .ls-systems-menu.vertical .systems-container .active-system {
            display: block;
            position: absolute;
            top: 0;
            left: 0;
        }

        .eip-button {
            position: relative;
        }
        .eip-modal {
            cursor: default;
        }
        .eip-modal div {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin: -8px;
            padding: 20px;
            width: 500px;
            background-color: #e0eaf9;
            border-radius: 3px;
        }
        @media (max-width: 576px) {
            .eip-modal div {
                width: 80vw;
            }
        }
        .eip-modal h3 {
            margin: 0;
            font-size: 18px;
        }
        .eip-modal p {
            font-weight: 400;
            text-wrap: wrap;
            font-size: 16px;
            margin: 0;
            word-break: break-all;
            overflow-wrap: anywhere;
            min-width: 0;
            white-space: normal;
        }

        .eip-modal img {
            margin-top: 8px;
        }

        .eip-modal button {
            align-self: end;
            font-size: 12px;
            font-weight: 500;
            color: #fff;
            border: none;
            border-radius: 5px;
            padding: 10px 20px;
            background: linear-gradient(270deg, #00adee 18.35%, #6b8be4 81.94%);
            cursor: pointer;
        }
    `;

    @state() public isSubMenuActive: boolean = false;
    @state() token: { token: string } = { token: '' };
    @state() isAccountCenter: boolean = false;
    @state() isShowEipModal: boolean = false;

    @property({ type: String }) clientId: string = '';
    @property({ type: Function }) getToken?: (loginPKS: (token: string) => void) => Promise<{ token: string }>;
    @property({ type: String }) direction: 'vertical' | 'horizontal' = 'horizontal';
    @property({ type: Object }) roleInfo: RoleInfo = {};

    private _handleTitleClick() {
        this.isSubMenuActive = !this.isSubMenuActive;
    }

    private async _handleSystemClick(clientId: string | undefined, e: Event) {
        if (clientId === 'pks') {
            e.preventDefault();
            if (this.getToken) this.token = await this.getToken(loginPKS);
            if (this.token) {
                loginPKS(this.token?.token);
            } else {
                loginPKS(undefined);
            }
        }
        if (clientId === 'eip') {
            e.preventDefault();
            this.isShowEipModal = true;
        }
    }

    private _goEipWebsite() {
        window.open(uris.EIP_URL, '_blank');
    }

    private _closeEipModal(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        this.isShowEipModal = false;
    }

    get activeSystem() {
        return mainSystems.find((system) => system.clientId === this.clientId);
    }

    connectedCallback(): void {
        super.connectedCallback();
        if (window.location.pathname === '/account' && window.location.search.includes('?tab')) {
            this.isAccountCenter = true;
        }
    }

    protected render() {
        const arrowUrl = getUrl(`assets/images/arrow.webp`);
        const eipHint = getUrl(`assets/images/eip-hint.webp`);

        return html`<div class="ls-systems-menu ${this.direction}">
            <div class="title" @click=${this._handleTitleClick}>
                磊山系統
                <img src=${arrowUrl} width="10" alt="arrow" class=${this.isSubMenuActive ? '' : '-rotate-90'} />
            </div>
            <div class="systems-container" style=${this.isAccountCenter ? '' : 'min-height: 40px;'}>
                <div class="systems-container-inner ${this.isSubMenuActive ? '' : 'deactivated'}">
                    <div class="systems">
                        ${mainSystems.map(
                            (system) => html`
                                <ls-systems-button
                                    class="system-item ${system.clientId}-button"
                                    name=${system.name}
                                    uri=${system.uri}
                                    target=${system.target}
                                    .active=${!this.isAccountCenter && this.clientId === system.clientId}
                                    style=${this.direction == 'vertical' &&
                                    this.clientId === system.clientId &&
                                    !this.isAccountCenter
                                        ? 'order: -1;'
                                        : ''}
                                    theme=${this.direction == 'vertical' ? 'primary' : 'secondary'}
                                    @click=${(e: Event) => this._handleSystemClick(system.clientId, e)}
                                >
                                    ${system.clientId === 'eip' && this.isShowEipModal
                                        ? html`<ls-modal class="eip-modal align="left" .onClose=${(e: Event) => this._closeEipModal(e)}>
                                              <div>
                                                <h3 @click="${this._closeEipModal}">親愛的夥伴您好</h3>

                                                <p>請點擊下方按鈕「點我前往線上報聘」進入報聘系統。</p>
                                                
                                                <p>若您在線上報聘系統操作時遇到Email錯誤提示，無法透過「下一步」按鈕存檔時，請透過線上報聘系統右上方頁籤(如下圖)切換頁面，如：點擊「申請內容」，即可前往下一分頁繼續操作與存檔。</p>
                                                
                                                <img src=${eipHint} alt="eip-hint" width="100%" />

                                                <button @click="${this._goEipWebsite}">點我前往 線上報聘</button>
                                              </div>
                                          </ls-modal>`
                                        : ''}
                                </ls-systems-button>
                            `,
                        )}
                        <ls-systems-sub-menu .roleInfo=${this.roleInfo}></ls-systems-sub-menu>
                    </div>
                </div>
                ${!this.isAccountCenter && this.clientId
                    ? html`<ls-systems-button
                          class="active-system"
                          name=${this.activeSystem?.name}
                          uri=${this.activeSystem?.uri}
                          .active=${true}
                          target=${this.activeSystem?.target}
                      />`
                    : ''}
            </div>
            <div class="background"></div>
        </div>`;
    }
}
